import type { CursorSearchResponseQuizBankSearchRes } from '@/api/gen/_model/cursorSearchResponseQuizBankSearchRes';
import type { MultipleSet } from '@/api/gen/_model/multipleSet';
import type { OxSet } from '@/api/gen/_model/oxSet';
import type { QuizBankAuthorRes } from '@/api/gen/_model/quizBankAuthorRes';
import type { QuizBankFacetsRes as GenQuizBankFacetsRes } from '@/api/gen/_model/quizBankFacetsRes';
import type { QuizBankItemRes as GenQuizBankItemRes } from '@/api/gen/_model/quizBankItemRes';
import type { QuizBankSearchReq } from '@/api/gen/_model/quizBankSearchReq';
import type { QuizBankSearchRes as GenQuizBankSearchRes } from '@/api/gen/_model/quizBankSearchRes';
import type { QuizBankStandardRes } from '@/api/gen/_model/quizBankStandardRes';
import type { QuizBankUnitFacet } from '@/api/gen/_model/quizBankUnitFacet';
import type { SubjectiveSet } from '@/api/gen/_model/subjectiveSet';
import { searchItems } from '@/api/gen/quiz-bank/quiz-bank.gen';

/**
 * 문제 은행 검색 API 의 null 보정 타입.
 *
 * springdoc 이 코틀린 `String?` 을 nullable 로 내보내지 않아 orval 생성 타입은
 * `?: T` 지만, 실제 응답은 `description: null` 처럼 명시적 null 을 담는다.
 * `@JsonInclude(NON_NULL)` 필드(`multipleData`·`hint`·`facets` 등)는 생략되므로
 * 생성 타입 그대로 두고, 나머지 nullable leaf 만 여기서 넓힌다.
 */
type WithNullable<T, K extends keyof T> = Omit<T, K> & {
  [P in K]?: T[P] | null;
};

type QuestionSet<T extends Pick<OxSet, 'question' | 'questionRichText'>> =
  WithNullable<T, 'question' | 'questionRichText'>;

type ChoiceRes = WithNullable<
  MultipleSet['choiceListV2'][number],
  'richText' | 'imageUrl'
>;

type MultipleDataRes = Omit<QuestionSet<MultipleSet>, 'choiceListV2'> & {
  choiceListV2: ChoiceRes[];
};

export type QuizBankItemRes = WithNullable<
  Omit<
    GenQuizBankItemRes,
    'standard' | 'author' | 'multipleData' | 'subjectiveData' | 'oxData'
  >,
  'imageUrl' | 'unitId' | 'unitName' | 'correctRate'
> & {
  standard: WithNullable<
    QuizBankStandardRes,
    'description' | 'areaCode' | 'areaName'
  >;
  author: WithNullable<QuizBankAuthorRes, 'avatarData'>;
  multipleData?: MultipleDataRes;
  subjectiveData?: QuestionSet<SubjectiveSet>;
  /** `answer` 가 정확히 o/x 가 아닐 수 있다 — 어댑터가 거른다. */
  oxData?: WithNullable<OxSet, 'question' | 'questionRichText' | 'answer'>;
};

export type QuizBankHintRes = NonNullable<GenQuizBankItemRes['hint']>;

/** `facets.units` 는 학년 → 학기 → 단원 순번 순서의 평탄한 목록이다. */
export type QuizBankUnitFacetRes = WithNullable<QuizBankUnitFacet, 'unitName'>;

export type QuizBankFacetsRes = Omit<GenQuizBankFacetsRes, 'units'> & {
  units: QuizBankUnitFacetRes[];
};

export type QuizBankCursorRes = Omit<
  CursorSearchResponseQuizBankSearchRes,
  'data'
> & {
  data: Omit<GenQuizBankSearchRes, 'items' | 'facets'> & {
    items: QuizBankItemRes[];
    facets?: QuizBankFacetsRes;
  };
};

export type { QuizBankSearchReq };

/**
 * 문항 검색. `cursor` 없는 첫 페이지에만 `facets`·`totalCount` 가 실려 오고,
 * 커서는 필터 조합에 묶여 있어 필터 변경 후 재사용하면 400 이다.
 */
export const searchQuizBankItems = (
  req: QuizBankSearchReq,
): Promise<QuizBankCursorRes> => searchItems(req);
